import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'wouter'

import { cn } from '@/lib/cn'
import { useNavItems } from '@/hooks'
import { type NavItem } from '@/lib/nav'

import { MenuIcon, UsersIcon } from './Icons'
import { NAV_ICONS, isActivePath } from './Sidebar'
import styles from './shell.module.css'

/** Pages that get a tab of their own; everything else lives behind "More". */
const TAB_PATHS: readonly string[] = ['/', '/playground', '/proxies', '/identities']

export interface MobileTabBarProps {
  /** Opens the navigation drawer with the full page list. */
  onOpenMore: () => void
  moreOpen: boolean
}

/**
 * TK mobile (patch 05): under 768px the sidebar is replaced by a bottom tab
 * bar - four everyday pages plus "More", which opens the full navigation in
 * the drawer. Sits above the home indicator via env(safe-area-inset-bottom).
 */
export function MobileTabBar({ onOpenMore, moreOpen }: MobileTabBarProps) {
  const { t } = useTranslation(['console', 'common'])
  const [location] = useLocation()
  const visible = useNavItems()
  const tabs = TAB_PATHS.map((path) => visible.find((item) => item.path === path)).filter(
    (item): item is NavItem => item !== undefined,
  )
  const tabActive = tabs.some((item) => isActivePath(location, item.path))
  // TK patch 06: one tab for all three BD panels.
  const bdActive = !moreOpen && isActivePath(location, '/bd-tools')

  return (
    <nav className={styles.tabBar} aria-label={t('common:nav.primary')}>
      {tabs.map((item) => {
        const Icon = NAV_ICONS[item.icon]
        const active = isActivePath(location, item.path)
        return (
          <Link
            key={item.path}
            href={item.path}
            className={cn(styles.tab, active && styles.tabActive)}
            aria-current={active ? 'page' : undefined}
          >
            <span className={styles.tabIcon}>
              <Icon size={20} />
            </span>
            <span className={styles.tabLabel}>{t(`console:${item.labelKey}`)}</span>
          </Link>
        )
      })}
      {visible.some((item) => item.bd) ? (
        <Link
          href="/bd-tools/screener"
          className={cn(styles.tab, bdActive && styles.tabActive)}
          aria-current={bdActive ? 'page' : undefined}
        >
          <span className={styles.tabIcon}>
            <UsersIcon size={20} />
          </span>
          <span className={styles.tabLabel}>{t('console:nav.bdTab')}</span>
        </Link>
      ) : null}
      <button
        type="button"
        className={cn(styles.tab, moreOpen || (!tabActive && !bdActive) ? styles.tabActive : undefined)}
        onClick={onOpenMore}
        aria-haspopup="dialog"
        aria-expanded={moreOpen}
        data-mobile-nav-trigger=""
      >
        <span className={styles.tabIcon}>
          <MenuIcon size={20} />
        </span>
        <span className={styles.tabLabel}>{t('console:nav.more')}</span>
      </button>
    </nav>
  )
}
